import { Controller } from "@hotwired/stimulus"

// Fee radio list: auto-fill amount from selected fee, disable online payment when a fee/other
// is selected, show/hide the "Other" reason field, and optionally lock the amount for "Full balance".
export default class extends Controller {
  static targets = [
    "feeRadio",
    "amount",
    "amountError",
    "otherReasonGroup",
    "otherReason",
    "taxWarning",
    "chargeSalesTaxCheckbox",
    "collectSalesTaxCheckbox",
    "collectTaxGroup",
    "manualTaxWrapper"
  ]
  static values = {
    feeAmounts: Object,
    balanceCents: Number,
    taxDefaultsEnabled: Boolean,
    salesTaxGuids: Array,
    taxableItemGuids: Array,
    fixedTaxPackageGuids: Array,
    fullBalanceUsesFixedTax: Boolean,
    pupilHasTaxedPayments: Boolean,
    lockFullBalanceAmount: Boolean
  }

  connect() {
    this.lastAppliedFeeGuid = null
    this.syncAll()
  }

  feeChanged() {
    this.syncAll()
  }

  taxChanged() {
    this.syncSalesTaxUx()
    this.element.dispatchEvent(new CustomEvent("balance-fee-context:changed"))
  }

  validateAmount(event) {
    const rawAmount = this.amountTarget.value.trim()
    const amount = Number(rawAmount)
    let message = null

    if (rawAmount === "" || !Number.isFinite(amount)) {
      message = "Enter an amount."
    } else if (amount < 0.01) {
      message = "Amount must be greater than $0.00."
    } else if (!/^\d+(\.\d{1,2})?$/.test(rawAmount)) {
      message = "Enter an amount with no more than two decimal places."
    }

    if (!message) return

    event.preventDefault()
    this.amountErrorTarget.textContent = message
    this.amountErrorTarget.classList.remove("u-hidden")
    this.amountTarget.focus()
  }

  clearAmountError() {
    this.amountErrorTarget.textContent = ""
    this.amountErrorTarget.classList.add("u-hidden")
  }

  // ── private helpers ──────────────────────────────────────────────────────────

  selectedFeeGuid() {
    const checked = this.feeRadioTargets.find(r => r.checked)
    return checked ? checked.value : null
  }

  selectedIsSalesTaxFee() {
    const guid = this.selectedFeeGuid()
    if (!guid) return false
    const guids = this.hasSalesTaxGuidsValue ? this.salesTaxGuidsValue : []
    return guids.includes(guid)
  }

  selectedIsFixedTaxPackage() {
    const guid = this.selectedFeeGuid()
    if (!guid) return false
    return guid === "full_balance" &&
      this.hasFullBalanceUsesFixedTaxValue &&
      this.fullBalanceUsesFixedTaxValue
  }

  selectedShouldChargeSalesTax() {
    const guid = this.selectedFeeGuid()
    if (!guid) return false
    if (guid === "full_balance") return false

    const guids = this.hasTaxableItemGuidsValue ? this.taxableItemGuidsValue : []
    return guids.includes(guid)
  }

  syncAll() {
    this.applyAmountFromFee()
    this.toggleOtherReason()
    this.syncAmountFieldState()
    this.syncSalesTaxUx()
    this.element.dispatchEvent(new CustomEvent("balance-fee-context:changed"))
  }

  // When the selected fee IS a sales tax fee, charging sales tax on top makes no sense:
  // hide and clear the manual "Charge sales tax" control. Otherwise, surface a soft warning
  // when this student has been taxed before but no tax is being collected on this payment.
  syncSalesTaxUx() {
    const selectedGuid = this.selectedFeeGuid()
    const selectionChanged = selectedGuid !== this.lastTaxSelectionGuid
    const isSalesTaxFee = this.selectedIsSalesTaxFee()
    const isFixedTaxPackage = this.selectedIsFixedTaxPackage()
    const taxDefaultsEnabled = this.hasTaxDefaultsEnabledValue && this.taxDefaultsEnabledValue
    const hideManualTax = isSalesTaxFee || isFixedTaxPackage ||
      (taxDefaultsEnabled && selectedGuid === "full_balance")

    if (this.hasManualTaxWrapperTarget) {
      this.manualTaxWrapperTarget.style.display = hideManualTax ? "none" : ""
    }
    if (this.hasChargeSalesTaxCheckboxTarget) {
      if (hideManualTax) {
        this.chargeSalesTaxCheckboxTarget.checked = false
      } else if (taxDefaultsEnabled && selectionChanged) {
        this.chargeSalesTaxCheckboxTarget.checked = this.selectedShouldChargeSalesTax()
      }

      if (taxDefaultsEnabled && this.chargeSalesTaxCheckboxTarget.checked) this.defaultTaxToOnTop()
      if (taxDefaultsEnabled || hideManualTax) this.syncManualTaxPanel()
    }

    if (this.hasCollectTaxGroupTarget) {
      this.collectTaxGroupTarget.dataset.automaticTax =
        taxDefaultsEnabled && selectedGuid === "full_balance" ? "true" : "false"
    }
    if (this.hasCollectSalesTaxCheckboxTarget && taxDefaultsEnabled && selectionChanged) {
      this.collectSalesTaxCheckboxTarget.checked = this.selectedShouldChargeSalesTax()
    }

    this.lastTaxSelectionGuid = selectedGuid

    if (!this.hasTaxWarningTarget) return

    const chargingTax =
      this.hasChargeSalesTaxCheckboxTarget && this.chargeSalesTaxCheckboxTarget.checked
    const shouldWarn =
      !taxDefaultsEnabled && !hideManualTax && this.pupilHasTaxedPaymentsValue && !chargingTax

    this.taxWarningTarget.classList.toggle("u-hidden", !shouldWarn)
  }

  defaultTaxToOnTop() {
    const onTop = this.element.querySelector(
      'input[name="manual_sales_tax_inclusive"][value="0"]'
    )
    if (onTop) onTop.checked = true
  }

  syncManualTaxPanel() {
    if (!this.hasManualTaxWrapperTarget || !this.hasChargeSalesTaxCheckboxTarget) return

    const panel = this.manualTaxWrapperTarget.querySelector(
      '[data-manual-sales-tax-gate-target="panel"]'
    )
    if (!panel) return

    const checked = this.chargeSalesTaxCheckboxTarget.checked
    panel.hidden = !checked
    panel.classList.toggle("u-hidden", !checked)
  }

  applyAmountFromFee() {
    const guid = this.selectedFeeGuid()
    if (!guid || guid === "other" || !this.hasAmountTarget) {
      this.lastAppliedFeeGuid = guid
      return
    }

    if (guid === "full_balance") {
      if (this.shouldLockFullBalanceAmount() || this.lastAppliedFeeGuid !== guid) {
        this.amountTarget.value = (this.balanceCentsValue / 100).toFixed(2)
      }
      this.lastAppliedFeeGuid = guid
      return
    }

    const amt = this.feeAmountsValue[guid]
    if (amt != null && amt !== "") {
      const parsed = Number(amt)
      this.amountTarget.value = Number.isFinite(parsed) ? parsed.toFixed(2) : amt
      this.lastAppliedFeeGuid = guid
    }
  }

  syncAmountFieldState() {
    if (!this.hasAmountTarget) return
    const isFullBalance = this.selectedFeeGuid() === "full_balance"
    // readonly keeps the value in the form submission; disabled would drop it
    this.amountTarget.readOnly = isFullBalance && this.shouldLockFullBalanceAmount()
  }

  shouldLockFullBalanceAmount() {
    return this.hasLockFullBalanceAmountValue ? this.lockFullBalanceAmountValue : true
  }

  toggleOtherReason() {
    if (!this.hasOtherReasonGroupTarget) return
    const isOther = this.selectedFeeGuid() === "other"
    this.otherReasonGroupTarget.style.display = isOther ? "" : "none"
    if (this.hasOtherReasonTarget) {
      this.otherReasonTarget.required = isOther
    }
    if (isOther && this.hasAmountTarget) {
      this.amountTarget.value = ""
    }
  }
}
